import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { registrationService } from '../../../services/registrationService';

const CoachInformationStep = ({ formData, errors, onChange, registrationId, onSave }) => {
  const [isSavingPrimaryCoach, setIsSavingPrimaryCoach] = useState(false);
  const [primaryCoachExists, setPrimaryCoachExists] = useState(false);
  const [isSavingSecondCoach, setIsSavingSecondCoach] = useState(false);
  const [secondCoachExists, setSecondCoachExists] = useState(false);
  const tshirtSizes = [
    { value: 's_36', label: 'S - Small', description: 'Chest: 36-38 inches' },
    { value: 'm_38', label: 'M - Medium', description: 'Chest: 38-40 inches' },
    { value: 'l_40', label: 'L - Large', description: 'Chest: 40-42 inches' },
    { value: 'xl_42', label: 'XL - Extra Large', description: 'Chest: 42-44 inches' },
    { value: 'xxl_44', label: 'XXL - Double XL', description: 'Chest: 44-46 inches' },
    { value: 'xxxl_46', label: 'XXXL - Triple XL', description: 'Chest: 46-48 inches' }
  ];

  // Check if coaches exist in database
  useEffect(() => {
    const checkCoaches = async () => {
      if (!registrationId) {
        setPrimaryCoachExists(false);
        setSecondCoachExists(false);
        return;
      }

      try {
        const existingCoaches = await registrationService?.getCoachesByRegistration(registrationId);
        const primaryCoach = existingCoaches?.find(c => c?.is_primary);
        const secondCoach = existingCoaches?.find(c => !c?.is_primary);
        setPrimaryCoachExists(!!primaryCoach);
        setSecondCoachExists(!!secondCoach && formData?.hasSecondCoach);
      } catch (error) {
        console.error('Error checking coaches:', error);
        setPrimaryCoachExists(false);
        setSecondCoachExists(false);
      }
    };

    checkCoaches();
  }, [registrationId, formData?.hasSecondCoach]);

  const handleAddSecondCoach = () => {
    onChange('hasSecondCoach', true);
  };

  const handleRemoveSecondCoach = async () => {
    if (!registrationId) {
      // If no registrationId, just update form state
      onChange('hasSecondCoach', false);
      onChange('secondCoachName', '');
      onChange('secondCoachMobile', '');
      onChange('secondCoachEmail', '');
      onChange('secondCoachTshirtSize', '');
      setSecondCoachExists(false);
      return;
    }

    try {
      // Get existing coaches and find the second coach
      const existingCoaches = await registrationService?.getCoachesByRegistration(registrationId);
      const secondCoach = existingCoaches?.find(c => !c?.is_primary);

      // Delete the second coach from database if it exists
      if (secondCoach) {
        await registrationService?.deleteCoach(secondCoach?.id);
      }

      // Update form state
      onChange('hasSecondCoach', false);
      onChange('secondCoachName', '');
      onChange('secondCoachMobile', '');
      onChange('secondCoachEmail', '');
      onChange('secondCoachTshirtSize', '');
      setSecondCoachExists(false);
      
      onSave?.(); // Trigger last saved update
    } catch (error) {
      console.error('Error removing second coach:', error);
      alert('Failed to remove coach. Please try again.');
    }
  };

  // Save/Update primary coach handler (explicit button click)
  const handleSavePrimaryCoach = async () => {
    if (!registrationId) return;

    // Validate required fields
    if (!formData?.primaryCoachName || !formData?.primaryCoachTshirtSize) {
      alert('Please fill in Coach Name and T-Shirt Size to save the primary coach.');
      return;
    }

    setIsSavingPrimaryCoach(true);

    try {
      const coachData = {
        name: formData?.primaryCoachName,
        tshirtSize: formData?.primaryCoachTshirtSize,
        isPrimary: true,
        email: formData?.primaryCoachEmail || null,
        phone: formData?.primaryCoachMobile || null
      };

      // Check if primary coach already exists
      const existingCoaches = await registrationService?.getCoachesByRegistration(registrationId);
      const primaryCoach = existingCoaches?.find(c => c?.is_primary);

      if (primaryCoach) {
        // Update existing primary coach
        await registrationService?.updateCoach(primaryCoach?.id, coachData);
      } else {
        // Add new primary coach
        await registrationService?.addCoach(registrationId, coachData);
        setPrimaryCoachExists(true);
      }

      onSave?.(); // Trigger last saved update
    } catch (error) {
      console.error('Error saving primary coach:', error);
      alert('Failed to save coach: ' + error?.message);
    } finally {
      setIsSavingPrimaryCoach(false);
    }
  };

  // Save/Update second coach handler (explicit button click)
  const handleSaveSecondCoach = async () => {
    if (!registrationId || !formData?.hasSecondCoach) return;

    // Validate required fields
    if (!formData?.secondCoachName || !formData?.secondCoachTshirtSize) {
      alert('Please fill in Coach Name and T-Shirt Size to save the second coach.');
      return;
    }

    setIsSavingSecondCoach(true);

    try {
      const coachData = {
        name: formData?.secondCoachName,
        tshirtSize: formData?.secondCoachTshirtSize,
        isPrimary: false,
        email: formData?.secondCoachEmail || null,
        phone: formData?.secondCoachMobile || null
      };

      // Check if second coach already exists
      const existingCoaches = await registrationService?.getCoachesByRegistration(registrationId);
      const secondCoach = existingCoaches?.find(c => !c?.is_primary);

      if (secondCoach) {
        // Update existing second coach
        await registrationService?.updateCoach(secondCoach?.id, coachData);
      } else {
        // Add new second coach
        await registrationService?.addCoach(registrationId, coachData);
        setSecondCoachExists(true);
      }

      onSave?.(); // Trigger last saved update
    } catch (error) {
      console.error('Error saving second coach:', error);
      alert('Failed to save coach: ' + error?.message);
    } finally {
      setIsSavingSecondCoach(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-primary font-semibold">2</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Coach Information</h3>
            <p className="text-sm text-muted-foreground">
              Provide details for the primary coach and optionally add a second coach
            </p>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6 space-y-6">
        <div className="space-y-4">
          <h4 className="text-base font-semibold text-foreground flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs">1</span>
            Primary Coach Details
          </h4>

          <Input
            label="Coach Name"
            type="text"
            placeholder="Enter coach full name"
            description="This will be auto-filled from POC registration"
            value={formData?.primaryCoachName}
            onChange={(e) => onChange('primaryCoachName', e?.target?.value)}
            error={errors?.primaryCoachName}
            required
          />

          <Input
            label="Mobile Number"
            type="tel"
            placeholder="9876543210"
            description="10-digit mobile number"
            value={formData?.primaryCoachMobile}
            onChange={(e) => onChange('primaryCoachMobile', e?.target?.value)}
            error={errors?.primaryCoachMobile}
            maxLength={10}
            required
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="coach@example.com"
            description="Optional email for communication"
            value={formData?.primaryCoachEmail}
            onChange={(e) => onChange('primaryCoachEmail', e?.target?.value)}
            error={errors?.primaryCoachEmail}
          />

          <Select
            label="T-Shirt Size"
            description="Select appropriate size based on measurements"
            placeholder="Choose size"
            options={tshirtSizes}
            value={formData?.primaryCoachTshirtSize}
            onChange={(value) => {
              onChange('primaryCoachTshirtSize', value);
            }}
            error={errors?.primaryCoachTshirtSize}
            required
          />

          <div className="pt-2">
            <Button
              variant="default"
              iconName={primaryCoachExists ? "Edit" : "Save"}
              iconPosition="left"
              onClick={handleSavePrimaryCoach}
              loading={isSavingPrimaryCoach}
              fullWidth
              disabled={!formData?.primaryCoachName || !formData?.primaryCoachTshirtSize}
            >
              {isSavingPrimaryCoach ? 'Saving...' : (primaryCoachExists ? 'Update Coach' : 'Save Coach')}
            </Button>
          </div>
        </div>

        {!formData?.hasSecondCoach ? (
          <div className="pt-4 border-t border-border">
            <Button
              variant="outline"
              iconName="Plus"
              iconPosition="left"
              onClick={handleAddSecondCoach}
              fullWidth
            >
              Add Second Coach (Optional)
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <h4 className="text-base font-semibold text-foreground flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-xs">2</span>
                Second Coach Details
              </h4>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={handleRemoveSecondCoach}
              >
                Remove
              </Button>
            </div>

            <Input
              label="Coach Name"
              type="text"
              placeholder="Enter coach full name"
              value={formData?.secondCoachName}
              onChange={(e) => onChange('secondCoachName', e?.target?.value)}
              error={errors?.secondCoachName}
            />

            <Input
              label="Mobile Number"
              type="tel"
              placeholder="9876543210"
              description="10-digit mobile number"
              value={formData?.secondCoachMobile}
              onChange={(e) => onChange('secondCoachMobile', e?.target?.value)}
              error={errors?.secondCoachMobile}
              maxLength={10}
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="coach@example.com"
              value={formData?.secondCoachEmail}
              onChange={(e) => onChange('secondCoachEmail', e?.target?.value)}
              error={errors?.secondCoachEmail}
            />

            <Select
              label="T-Shirt Size"
              placeholder="Choose size"
              options={tshirtSizes}
              value={formData?.secondCoachTshirtSize}
              onChange={(value) => {
                onChange('secondCoachTshirtSize', value);
              }}
              error={errors?.secondCoachTshirtSize}
            />

            <div className="pt-2">
              <Button
                variant="default"
                iconName={secondCoachExists ? "Edit" : "Save"}
                iconPosition="left"
                onClick={handleSaveSecondCoach}
                loading={isSavingSecondCoach}
                fullWidth
                disabled={!formData?.secondCoachName || !formData?.secondCoachTshirtSize}
              >
                {isSavingSecondCoach ? 'Saving...' : (secondCoachExists ? 'Update Coach' : 'Save Coach')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachInformationStep;